import { useCallback } from "react";
import axios from "axios";

const API = process.env.REACT_APP_API || "https://www.vilaro.ro/api";

// ============================================================
// useCommentActions — hook pentru toate acțiunile pe comentarii
// ============================================================
// Folosit de Inbox și Istoric pentru a reduce duplicarea logicii CRUD.
//
// Params:
//   onAfterAction — callback apelat după fiecare acțiune reușită (ex: refresh)
//   getPageId     — funcție (commentId) => pageId, pentru contexte diferite
//   whitelistIds  — Set de from_id protejați (nu pot fi hide/block)
//
// Returnează: { hide, unhide, block, unblock, resolve, unresolve, delete:del,
//               reply, postNewComment, isWhitelisted }
// ============================================================
export function useCommentActions({ onAfterAction, getPageId, whitelistIds }) {

  const isWhitelisted = useCallback((fromId) => {
    if (!fromId || !whitelistIds) return false;
    return whitelistIds.has ? whitelistIds.has(fromId) : false;
  }, [whitelistIds]);

  const hide = useCallback(async (commentId) => {
    const pageId = getPageId(commentId);
    if (!pageId) throw new Error("No page_id");
    await axios.post(`${API}/comments/${commentId}/hide?page_id=${pageId}`);
    if (onAfterAction) await onAfterAction();
  }, [getPageId, onAfterAction]);

  const unhide = useCallback(async (commentId) => {
    const pageId = getPageId(commentId);
    if (!pageId) throw new Error("No page_id");
    await axios.post(`${API}/comments/${commentId}/unhide?page_id=${pageId}`);
    if (onAfterAction) await onAfterAction();
  }, [getPageId, onAfterAction]);

  const block = useCallback(async (commentId) => {
    const pageId = getPageId(commentId);
    if (!pageId) throw new Error("No page_id");
    await axios.post(`${API}/comments/${commentId}/block-user?page_id=${pageId}`);
    if (onAfterAction) await onAfterAction();
  }, [getPageId, onAfterAction]);

  const unblock = useCallback(async (commentId) => {
    const pageId = getPageId(commentId);
    if (!pageId) throw new Error("No page_id");
    await axios.post(`${API}/comments/${commentId}/unblock-user?page_id=${pageId}`);
    if (onAfterAction) await onAfterAction();
  }, [getPageId, onAfterAction]);

  const resolve = useCallback(async (commentId) => {
    await axios.post(`${API}/comments/${commentId}/resolve`);
    if (onAfterAction) await onAfterAction();
  }, [onAfterAction]);

  const unresolve = useCallback(async (commentId) => {
    await axios.post(`${API}/comments/${commentId}/unresolve`);
    if (onAfterAction) await onAfterAction();
  }, [onAfterAction]);

  const del = useCallback(async (commentId) => {
    const pageId = getPageId(commentId);
    if (!pageId) throw new Error("No page_id");
    await axios.delete(`${API}/comments/${commentId}?page_id=${pageId}`);
    if (onAfterAction) await onAfterAction();
  }, [getPageId, onAfterAction]);

  const reply = useCallback(async (commentId, message, pageId) => {
    const pid = pageId || getPageId(commentId);
    if (!pid) throw new Error("No page_id");
    await axios.post(`${API}/comments/${commentId}/reply?page_id=${pid}`, { message });
    if (onAfterAction) await onAfterAction();
  }, [getPageId, onAfterAction]);

  const postNewComment = useCallback(async (postId, message, pageId) => {
    await axios.post(`${API}/posts/${postId}/comment?page_id=${pageId}`, { message });
    if (onAfterAction) await onAfterAction();
  }, [onAfterAction]);

  return {
    hide,
    unhide,
    block,
    unblock,
    resolve,
    unresolve,
    delete: del,
    reply,
    postNewComment,
    isWhitelisted,
  };
}
